// src/components/FallingText.jsx
import { useRef, useState, useEffect } from "react";
import Matter from "matter-js";

const nextFrame = () =>
  new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));

export default function FallingText({
  text = "",
  highlightWords = [],
  gravity = 1,
  trigger = "auto",
  fontSize = "22px",
  backgroundColor = "#06071d",
  wireframes = false,
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const canvasRef = useRef(null);
  const [started, setStarted] = useState(false);

  /* ---------------------------------------------
     CREATE SPANS (only once)
  --------------------------------------------- */
  useEffect(() => {
    if (!textRef.current) return;

    const words = text.split(/\s+/);

    const html = words
      .map((w) => {
        const isHighlighted = highlightWords.includes(w);
        return `<span class="ft-word ${isHighlighted ? "highlighted" : ""}">${w}</span>`;
      })
      .join(" ");

    textRef.current.innerHTML = html;
  }, [text, highlightWords]);

  /* ---------------------------------------------
     AUTO START
  --------------------------------------------- */
// Start only when section becomes visible
useEffect(() => {
  if (trigger !== "onVisible") return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setStarted(true);
        }
      });
    },
    { threshold: 0.3 } // 30% visible to trigger
  );

  if (containerRef.current) {
    observer.observe(containerRef.current);
  }

  return () => {
    if (containerRef.current) observer.unobserve(containerRef.current);
  };
}, [trigger]);


  /* ---------------------------------------------
     MAIN PHYSICS
  --------------------------------------------- */
  useEffect(() => {
    if (!started) return;

    const container = containerRef.current;
    const textLayer = textRef.current;
    const canvasHolder = canvasRef.current;

    if (!container || !textLayer || !canvasHolder) return;

    let engine, render, runner;
    let mounted = true;

    (async () => {
      await nextFrame(); // allow DOM to layout

      const containerRect = container.getBoundingClientRect();
      const width = containerRect.width;
      const height = containerRect.height;

      if (!width || !height) return;

      const spans = Array.from(textLayer.querySelectorAll(".ft-word"));

      // Set words absolute for animation mapping
      spans.forEach((span) => {
        const r = span.getBoundingClientRect();
        span.style.position = "absolute";
        span.style.left = r.left - containerRect.left + "px";
        span.style.top = r.top - containerRect.top + "px";
        span.style.transform = "translate(-50%, -50%)";
        span.style.zIndex = 5;
        span.style.pointerEvents = "none";
      });

      // Setup Matter.js engine
      const { Engine, Render, World, Bodies, Mouse, MouseConstraint, Runner } =
        Matter;

      engine = Engine.create();
      engine.world.gravity.y = gravity;

      render = Render.create({
        element: canvasHolder,
        engine,
        options: {
          width,
          height,
          background: backgroundColor,
          wireframes,
        },
      });

      // Walls
      const wallThickness = 500;  // huge invisible walls to block escape

const walls = [
  Bodies.rectangle(width / 2, height + wallThickness / 2, width * 2, wallThickness, {
    isStatic: true,
  }),
  Bodies.rectangle(width / 2, -wallThickness / 2, width * 2, wallThickness, {
    isStatic: true,
  }),
  Bodies.rectangle(-wallThickness / 2, height / 2, wallThickness, height * 2, {
    isStatic: true,
  }),
  Bodies.rectangle(width + wallThickness / 2, height / 2, wallThickness, height * 2, {
    isStatic: true,
  }),
];

      // Convert spans into bodies
      const wordBodies = spans.map((span) => {
        const r = span.getBoundingClientRect();
        const w = r.width || 40;
        const h = r.height || 20;

        const body = Bodies.rectangle(
          r.left - containerRect.left + w / 2,
          r.top - containerRect.top + h / 2,
          w,
          h,
          {
            restitution: 0.6,
            frictionAir: 0.015,
            render: { fillStyle: "transparent", strokeStyle: "transparent" },
          }
        );

        return { span, body };
      });

      World.add(engine.world, [...walls, ...wordBodies.map((wb) => wb.body)]);

      // Mouse drag interaction
      const mouse = Mouse.create(render.canvas);
      const mc = MouseConstraint.create(engine, {
        mouse,
        constraint: {
          stiffness: 0.15,
          render: { visible: false },
        },
      });
      World.add(engine.world, mc);

      // Run engine
      runner = Runner.create();
      Runner.run(runner, engine);
      Render.run(render);

      // Update DOM spans with physics body positions
      const updateLoop = () => {
        if (!mounted) return;
        wordBodies.forEach(({ span, body }) => {
          span.style.left = `${body.position.x}px`;
          span.style.top = `${body.position.y}px`;
          span.style.transform = `translate(-50%,-50%) rotate(${body.angle}rad)`;
        });
        requestAnimationFrame(updateLoop);
      };
      updateLoop();
    })();

    return () => {
      mounted = false;

      if (render) {
        Matter.Render.stop(render);
        try {
          render.canvas.remove();
        } catch (_) {}
      }

      if (engine) {
        Matter.World.clear(engine.world, false);
        Matter.Engine.clear(engine);
      }
    };
  }, [started, gravity, backgroundColor, wireframes]);

  return (
    <div
      ref={containerRef}
      className="falling-text-container"
      style={{
        position: "relative",
        width: "100%",
        height: "400px",
        overflow: "hidden",
      }}
      onClick={() => trigger === "click" && setStarted(true)}
      onMouseEnter={() => trigger === "hover" && setStarted(true)}
    >
      {/* text layer */}
      <div
        ref={textRef}
        style={{
          fontSize,
          color: "#fff",
          padding: "20px",
          lineHeight: 1.4,
        }}
      />

      {/* Matter.js canvas */}
      <div
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          pointerEvents: "auto",
        }}
      />
    </div>
  );
}
